import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Icon, { SettingFilled } from '@ant-design/icons';
import { EdgeFunctionWidgetState } from '@kemu-io/kemu-core/types';
import {
	getDefaultState,
} from '@kemu-io/kemu-core/widgets/edgeFunction/index.js';
import { CustomWidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import { DataType } from '@kemu-io/hs-types';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import {
	GateCustomSettingsProps,
	GetPortsInformationFunction,
	GateUI,
	GateUIProps
} from '../index.ts';
import { ReactComponent as EdgeFunctionWidgetIcon } from './icon.svg';
import styles from './edgeFunction.module.css';
import TextInput from '@components/WidgetsComponents/TextInput/TextInput';
import GateIcon from '@components/gateIcon/gateIcon';
import useTranslation from '@hooks/useTranslation';
import EdgeFunctionProcessor from '@kemu-io/kemu-core/widgets/edgeFunction/index.js';
import { PortLocation } from '@src/types/canvas_t';
import { SETTINGS_CONTAINER_CLASS } from '@common/constants';
import FormGroup from '@components/form-control/formGroup/formGroup';

type Props = GateUIProps

const EdgeFunctionWidget = (props: Props): React.JSX.Element => {
	const [state] = useReactiveWidgetState<EdgeFunctionWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);
	const fixedState: EdgeFunctionWidgetState = {
		...getDefaultState(),
		...state,
	};

	return (
		<div className={styles.GateBody}>
			<div className={styles.InnerWidgetBody}>
				<span className={styles.SettingsBtn} onClick={props.openCustomSettings}>
					<SettingFilled />
				</span>
				<div className={styles.InnerBody}>
					<Icon component={EdgeFunctionWidgetIcon} className={styles.Icon} />
				</div>
			</div>
			<div className={classNames(styles.BodyLabel, {
				[styles.InFolder]: props.info.groupId
			})}>
				{fixedState.functionName}
			</div>
		</div>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={EdgeFunctionWidgetIcon} />}/>
	);
};

const GateCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<EdgeFunctionWidgetState>(props.recipeId, props.blockId, props.gateInfo.id);
	const fixedState = useMemo(() => ({
		...getDefaultState(),
		...state
	}), [state]);

	const t = useTranslation('LogicMapper.Gates.EdgeFunction.Settings');

	const updateFunctionName = useCallback((functionName: string) => {
		setState((s) => ({
			...s,
			functionName,
		}));
	}, [setState]);

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}
			<div className={styles.SettingsContent}>
				<FormGroup noMarginBottom>
					<label className={styles.Label}>
						{t('FunctionNameLabel', 'Function Name')}
					</label>
					<TextInput
						value={fixedState.functionName}
						placeholder={t('FunctionNamePlaceholder', 'Enter function name')}
						onChange={(e) => updateFunctionName(e.currentTarget.value)}
					/>
				</FormGroup>
			</div>
		</div>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state: CustomWidgetState<EdgeFunctionWidgetState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = EdgeFunctionProcessor.getOutputNames(state, portContext);
	const inputNames = EdgeFunctionProcessor.getInputNames(state, portContext);

	// Calculate input positions - spread evenly on the left
	const inputPositions: Record<string, PortLocation> = {};
	const totalInputs = inputNames.length || 1;
	inputNames.forEach((input, i) => {
		inputPositions[input.name] = [0, (i + 1) / (totalInputs + 1), -1, 0];
	});

	// Calculate output positions - spread evenly on the right
	const outputPositions: Record<string, PortLocation> = {};
	const totalOutputs = outputNames.length || 1;
	outputNames.forEach((output, i) => {
		outputPositions[output.name] = [1, (i + 1) / (totalOutputs + 1), 1, 0];
	});

	return {
		outputs: outputNames.map((output) => {
			return {
				name: output.name,
				type: output.type,
				position: outputPositions[output.name] || [1, 0.5, 1, 0],
			};
		}),

		inputs: inputNames.map((input) => {
			return {
				name: input.name,
				type: input.type,
				position: inputPositions[input.name] || [0, 0.5, -1, 0],
			};
		})
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: EdgeFunctionWidget,
	CustomSettingsDialog: GateCustomSettings,
	hasTitle: false,
	settingsCustomSize: true,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Gates.EdgeFunction.Title', defaultMessage: 'Edge Function' }),
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Gates.EdgeFunction.Title', defaultMessage: 'Edge Function' }),
	getWrapperClass: () => styles.WidgetWrapper,
} as GateUI;
